import React from 'react';
import { useMemo } from 'react';
import type { Headers } from 'react-csv/lib/core';
import dayjs from 'dayjs';
import CsvDownloadButton, {
  csvFilename,
} from 'pages/objectManager/components/JobResults/pages/components/CsvDownloadButton';
import JobErrorTable, {
  compareExtId,
} from 'pages/objectManager/components/JobResults/pages/components/JobErrorTable';
import useErrorTableData, {
  countFailedObjects,
} from 'pages/objectManager/components/JobResults/pages/hooks/useErrorTableData';
import type { TObjectImportJob } from 'types/objectJob.type';
import { Typography } from '@timeedit/ui-components';
import { Space, Spin } from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';
import './ValidationResult.scss';

const HEADERS: Headers = [
  { key: 'extId', label: 'Ext. ID' },
  { key: 'column', label: 'Column' },
  { key: 'cell', label: 'Cell' },
  { key: 'message', label: 'Message' },
];

type Props = {
  job: TObjectImportJob;
};

const ValidationResult: React.FC<Props> = (props) => {
  const awaitingValidation = ['VALIDATE_QUEUED', 'VALIDATING'].includes(
    props.job.status
  );
  const failed = props.job.status === 'FAILED';
  const { objectsToProcess } = props.job;
  const errorData = useErrorTableData(props.job);
  const validationErrors = useMemo(
    () => errorData.filter((d) => d.kind === 'VALIDATION'),
    [errorData]
  );

  const failedObjects = useMemo(
    () => countFailedObjects(validationErrors),
    [validationErrors]
  );

  const csvRows = useMemo(
    () =>
      validationErrors
        .map(({ extId, column, cell, message }) => ({
          extId: extId ?? '',
          column: column ?? '',
          cell: cell ?? '',
          message,
        }))
        .sort((a, b) => compareExtId(a.extId, b.extId)),
    [validationErrors]
  );

  return (
    <>
      <div className="validation-result-title">
        <Typography.Title level={4}>Validation Result</Typography.Title>{' '}
        <CsvDownloadButton
          rows={csvRows}
          headers={HEADERS}
          filename={csvFilename([
            dayjs(props.job.queuedAt).format('YYYY-MM-DD_HH_mm_ss'),
            props.job.objectTypeName,
            props.job.operationType,
            'validation',
          ])}
          enabled={!awaitingValidation && csvRows.length > 0}
          disabledTooltip={
            awaitingValidation ? undefined : 'No validation errors to download.'
          }
        />
      </div>
      <div className="validation-result-box">
        {awaitingValidation && (
          <Space>
            <Spin />
            <Typography.Text>File validation in progress</Typography.Text>
          </Space>
        )}
        {!awaitingValidation && (
          <>
            {!failed && (
              <LabelWrapper label="Object updates">
                <b>
                  {objectsToProcess.length - failedObjects} will be processed
                </b>
              </LabelWrapper>
            )}
            <LabelWrapper label="Failed object validations">
              <b>{failedObjects}</b>
            </LabelWrapper>
            <div className="validation-result-errors">
              <JobErrorTable
                data={validationErrors}
                title="Failed object validations"
                groupByObject
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ValidationResult;
